import React, { useState } from "react";
import { convertTimestampToDate } from "../../../../utils";
import { FaTrashAlt, FaHourglassHalf } from "react-icons/fa";
import { OvalLoader } from "../../../Common/Loaders";
import {
  useFetchProjectBidsQuery,
  useDeleteProjectBidMutation,
  useDeleteProjectQuestionMutation,
} from "../../../../services/baseQuery";
import ProjectQuestions from "./ProjectQuestions";
import ProjectDetailsBox from "./ProjectDetailsBox";

export default function PostedProjectDetails({ project }) {
  const [activeTab, setActiveTab] = useState("BIDS");
  const [showModal, setShowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  // Replace the manual fetch with RTK Query hook
  const { data: bids, isLoading: loading } = useFetchProjectBidsQuery(
    { projectId: project.id },
    { skip: !project.adminApproved }
  );

  // Add delete mutation hooks
  const [deleteProjectBid, { isLoading: deleteLoading }] =
    useDeleteProjectBidMutation();
  const [deleteProjectQuestion, { isLoading: deleteQuestionLoading }] =
    useDeleteProjectQuestionMutation();

  const openDeleteModal = (id, type) => {
    setItemToDelete(id);
    setDeleteType(type);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === "bid") {
        await deleteProjectBid({
          bidId: itemToDelete,
          projectId: project.id,
        }).unwrap();
      } else if (deleteType === "question") {
        await deleteProjectQuestion({
          projectId: project.id,
          questionId: itemToDelete,
        }).unwrap();
      }

      setShowModal(false);
      setItemToDelete(null);
      setDeleteType("");
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
      // Handle error (maybe show a toast notification)
    }
  };

  const handleCancelDelete = () => {
    setShowModal(false);
    setItemToDelete(null);
    setDeleteType("");
  };

  // Custom Modal Component
  const DeleteConfirmationModal = () => {
    if (!showModal) return null;

    return (
      <div className="modalOverlay">
        <div className="modalContent">
          <div className="modalHeader">
            <h3>Confirm Delete</h3>
          </div>
          <div className="modalBody">
            <p>Are you sure you want to delete this {deleteType}?</p>
            <p>This action cannot be undone.</p>
          </div>
          <div className="modalFooter">
            <button
              className="cancelButton"
              onClick={handleCancelDelete}
              disabled={deleteLoading || deleteQuestionLoading}
            >
              Cancel
            </button>
            <button
              className="confirmDeleteButton"
              onClick={handleConfirmDelete}
              disabled={deleteLoading || deleteQuestionLoading}
            >
              {deleteLoading || deleteQuestionLoading ? (
                <div className="deleteLoaderContainer">
                  <OvalLoader color="#fff" />
                  <span>Deleting...</span>
                </div>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="projectDetailsContainer">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal />

      {/* Project ID and title */}
      <div className="headerWithBack">
        <div className="headerProjectTitle">{project.title}</div>
        <div className="projectId">#{project.projectId}</div>
      </div>

      {/* Project details box */}
      <ProjectDetailsBox project={project} />

      {/* Bids and questions tabs - Only show if admin approved */}
      {project.adminApproved ? (
        <div className="bidContainer">
          <div className="bidTabs">
            <div
              className={`bidTab ${activeTab === "BIDS" ? "activeBidTab" : ""}`}
              onClick={() => setActiveTab("BIDS")}
            >
              Biddings
            </div>
            <div
              className={`bidTab ${
                activeTab === "QUESTIONS" ? "activeBidTab" : ""
              }`}
              onClick={() => setActiveTab("QUESTIONS")}
            >
              Questions
            </div>
          </div>

          {/* Bids content */}
          {activeTab === "BIDS" && (
            <div>
              {loading ? (
                <div className="loaderContainer">
                  <OvalLoader />
                </div>
              ) : bids && bids.length > 0 ? (
                bids.map(bid => (
                  <div className="bidCard" key={bid.id}>
                    <div className="bidHeader">
                      <div className="bidMerchantInfo">
                        <img
                          src={
                            bid.merchantData.displayImage ||
                            require("../../../../assets/profile.png")
                          }
                          alt="merchant"
                          className="bidMerchantImage"
                        />
                        <div className="bidMerchantName">
                          {bid.merchantData.displayName}
                        </div>
                      </div>
                      <div className="bidAmountContainer">
                        <div className="bidAmount">{bid.bidPrice} SAR</div>
                        <div className="bidFees">
                          Admin Fee: {bid.projectFeesAtBidding}%
                          <span className="feesAmount">
                            (
                            {(
                              (bid.bidPrice * bid.projectFeesAtBidding) /
                              100
                            ).toFixed(2)}{" "}
                            SAR)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bidFooter">
                      <div className="bidDate">
                        {convertTimestampToDate(bid.createdAt)}
                      </div>
                      <button
                        className="deleteButton"
                        onClick={() => openDeleteModal(bid.id, "bid")}
                      >
                        <FaTrashAlt /> Delete Bid
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="emptyState">
                  <div className="emptyStateText">
                    No bids available for this project
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Questions content */}
          {activeTab === "QUESTIONS" && (
            <ProjectQuestions
              project={project}
              openDeleteModal={openDeleteModal}
            />
          )}
        </div>
      ) : (
        <div className="pendingApprovalContainer">
          <div className="pendingApprovalContent">
            <div className="pendingApprovalIconContainer">
              <FaHourglassHalf className="pendingIcon" />
            </div>
            <h3>Pending Admin Approval</h3>
            <p>
              This project is currently under review. Bids and questions will be
              available once approved by admin.
            </p>
          </div>
        </div>
      )}

      <style jsx>{`
        .modalOverlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modalContent {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          width: 400px;
          max-width: 90%;
        }

        .modalHeader {
          padding: 16px 20px;
          border-bottom: 1px solid #eaeaea;
        }

        .modalHeader h3 {
          margin: 0;
          font-size: 18px;
          color: #333;
          font-family: inherit;
        }

        .modalBody {
          padding: 20px;
        }

        .modalBody p {
          margin: 0 0 10px;
          color: #555;
          font-family: inherit;
        }

        .modalFooter {
          padding: 16px 20px;
          border-top: 1px solid #eaeaea;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .cancelButton,
        .confirmDeleteButton {
          padding: 8px 16px;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: background-color 0.2s;
          font-family: inherit;
        }

        .cancelButton {
          background-color: #f0f0f0;
          color: #333;
        }

        .cancelButton:hover {
          background-color: #e0e0e0;
        }

        .confirmDeleteButton {
          background-color: #ff4d4f;
          color: white;
        }

        .confirmDeleteButton:hover {
          background-color: #ff7875;
        }

        .bidFooter {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #f0f0f0;
        }

        .deleteButton {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          background-color: #fff0f0;
          color: #ff4d4f;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          font-size: 14px;
        }

        .deleteButton:hover {
          background-color: #ffdbdb;
        }

        .deleteButton svg {
          font-size: 16px;
        }

        .deleteLoaderContainer {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .deleteLoaderContainer span {
          font-size: 14px;
        }

        .confirmDeleteButton:disabled,
        .cancelButton:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .confirmDeleteButton {
          min-width: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .vatStatus,
        .adminStatus {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 14px;
          font-weight: 500;
        }

        .vatStatus.included {
          background-color: #e6f7ff;
          color: #1890ff;
        }

        .vatStatus.excluded {
          background-color: #fff7e6;
          color: #fa8c16;
        }

        .adminStatus.approved {
          background-color: #f6ffed;
          color: #52c41a;
        }

        .adminStatus.pending {
          background-color: #fff7e6;
          color: #fa8c16;
        }

        .pendingApprovalIconContainer {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #fff7e6;
          border-radius: 50%;
          margin-bottom: 16px;
        }

        .pendingApprovalIconContainer :global(.pendingIcon) {
          font-size: 32px;
          color: #fa8c16;
        }

        .pendingApprovalContainer {
          margin-top: 24px;
          padding: 32px;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .pendingApprovalContent {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 32px;
          background-color: #fafafa;
          border-radius: 8px;
          border: 1px dashed #d9d9d9;
        }

        .pendingApprovalContent h3 {
          margin: 0 0 8px;
          color: #fa8c16;
          font-size: 18px;
        }

        .pendingApprovalContent p {
          margin: 0;
          color: #666;
          font-size: 14px;
          max-width: 400px;
          line-height: 1.5;
        }

        .bidCard {
          margin-bottom: 24px;
          border: 1px solid #f0f0f0;
          border-radius: 8px;
          padding: 16px;
          background-color: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .bidAmountContainer {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }

        .bidAmount {
          font-size: 18px;
          font-weight: 600;
          color: #1890ff;
        }

        .bidFees {
          font-size: 14px;
          color: #666;
        }

        .feesAmount {
          margin-left: 4px;
          color: #52c41a;
          font-weight: 500;
        }

        .loaderContainer {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
          width: 100%;
        }

        .emptyState {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px;
          text-align: center;
        }

        .emptyStateText {
          color: #8c8c8c;
          font-size: 16px;
          margin-top: 16px;
        }
      `}</style>
    </div>
  );
}
